'use client'

import { useId, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { CheckCircle2, Loader2 } from 'lucide-react'

import { submitLead } from '@/app/actions/leads'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { LandingMessages } from '@/lib/marketing/landing-messages'

import type { LeadFormData as FormData } from './lead-form-schema'

// The client schema (zod, ~55 KB gzipped) loads when the visitor first
// touches the form, not with the page (ALU-110): the resolver awaits it, and
// focusing any field starts the download. Same schema, same messages.
const loadSchema = () => import('./lead-form-schema')
const resolver: Resolver<FormData> = async (values, context, options) =>
  (await loadSchema()).leadFormResolver(values, context, options)

const SIZES = [
  ['0-100', 'size0to100'],
  ['101-1000', 'size101to1000'],
  ['1001-2000', 'size1001to2000'],
  ['2001-5000', 'size2001to5000'],
  ['5001-7500', 'size5001to7500'],
  ['7500+', 'size7500plus'],
] as const

const labelClass = 'text-en-label font-semibold text-en-ink'
const errorClass = 'text-en-meta text-en-danger'

/**
 * "Request a demo" in Encuentro: the same fields, the same client schema and
 * the same action (`submitLead`) as the classic form. Results are said in
 * place (the classic uses toasts): an inline error with `role="alert"`, and a
 * thank-you card that replaces the form.
 */
export function LeadForm({ copy }: { copy: LandingMessages['leadForm'] }) {
  const id = useId()
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useForm<FormData>({
    resolver,
    defaultValues: {
      fullName: '',
      email: '',
      linkedinUrl: '',
      orgName: '',
      role: '',
      networkSize: '',
    },
  })
  const errors = form.formState.errors

  const onSubmit = async (data: FormData) => {
    setSubmitting(true)
    setError(null)
    try {
      const result = await submitLead(data)
      if (result.error) setError(result.error)
      else setDone(true)
    } catch {
      setError(copy.errorDescription)
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="grid justify-items-center gap-4 rounded-en-lg bg-en-surface p-8 text-center text-en-ink shadow-en-overlay">
        <span className="flex size-12 items-center justify-center rounded-en-full bg-en-positive-soft text-en-positive">
          <CheckCircle2 aria-hidden="true" className="size-6" />
        </span>
        <h3 className="text-en-heading">{copy.thankYou}</h3>
        <p className="max-w-xs text-en-ink-muted" role="status">
          {copy.thankYouDescription}
        </p>
        <Button look="encuentro" variant="secondary" onClick={() => setDone(false)}>
          {copy.sendAnother}
        </Button>
      </div>
    )
  }

  const field = (name: keyof FormData) => ({
    id: `${id}-${name}`,
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? `${id}-${name}-error` : undefined,
  })
  const fieldError = (name: keyof FormData) =>
    errors[name] ? (
      <p id={`${id}-${name}-error`} className={errorClass}>
        {errors[name]?.message}
      </p>
    ) : null

  return (
    <div className="grid gap-6 rounded-en-lg bg-en-surface p-6 text-en-ink shadow-en-overlay md:p-8">
      <div className="grid gap-1">
        <h3 className="text-en-heading">{copy.title}</h3>
        <p className="text-en-meta text-en-ink-muted">{copy.subtitle}</p>
      </div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        onFocus={() => void loadSchema()}
        className="grid gap-4"
        noValidate
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <label htmlFor={`${id}-fullName`} className={labelClass}>
              {copy.fullName}
            </label>
            <Input
              look="encuentro"
              autoComplete="name"
              placeholder={copy.placeholderName}
              {...field('fullName')}
              {...form.register('fullName')}
            />
            {fieldError('fullName')}
          </div>
          <div className="grid gap-2">
            <label htmlFor={`${id}-email`} className={labelClass}>
              {copy.workEmail}
            </label>
            <Input
              look="encuentro"
              type="email"
              autoComplete="email"
              placeholder={copy.placeholderEmail}
              {...field('email')}
              {...form.register('email')}
            />
            {fieldError('email')}
          </div>
        </div>
        <div className="grid gap-2">
          <label htmlFor={`${id}-linkedinUrl`} className={labelClass}>
            {copy.linkedinUrl}
          </label>
          <Input
            look="encuentro"
            type="url"
            placeholder={copy.placeholderLinkedin}
            {...field('linkedinUrl')}
            {...form.register('linkedinUrl')}
          />
          {fieldError('linkedinUrl')}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <label htmlFor={`${id}-orgName`} className={labelClass}>
              {copy.organizationName}
            </label>
            <Input
              look="encuentro"
              autoComplete="organization"
              placeholder={copy.placeholderOrg}
              {...field('orgName')}
              {...form.register('orgName')}
            />
            {fieldError('orgName')}
          </div>
          <div className="grid gap-2">
            <label htmlFor={`${id}-role`} className={labelClass}>
              {copy.yourRole}
            </label>
            <Input
              look="encuentro"
              autoComplete="organization-title"
              placeholder={copy.placeholderRole}
              {...field('role')}
              {...form.register('role')}
            />
            {fieldError('role')}
          </div>
        </div>
        <div className="grid gap-2">
          <label htmlFor={`${id}-networkSize`} className={labelClass}>
            {copy.networkSize}
          </label>
          <select
            {...field('networkSize')}
            {...form.register('networkSize')}
            className="min-h-en-control w-full rounded-en-md border border-en-line-control bg-en-surface px-4 py-2 text-en-body text-en-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
          >
            <option value="">{copy.selectSize}</option>
            {SIZES.map(([value, key]) => (
              <option key={value} value={value}>
                {copy[key]}
              </option>
            ))}
          </select>
        </div>
        {error ? (
          <p
            role="alert"
            className="rounded-en-md bg-en-danger-soft px-4 py-3 text-en-meta text-en-danger"
          >
            <span className="font-semibold">{copy.errorTitle}.</span> {error}
          </p>
        ) : null}
        <Button look="encuentro" variant="primary" type="submit" block disabled={submitting}>
          {submitting ? <Loader2 aria-hidden="true" className="animate-spin" /> : null}
          {copy.requestFreeDemo}
        </Button>
      </form>
    </div>
  )
}
